import React, { useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, PieController, ArcElement } from 'chart.js';
import { ChartOptions } from 'chart.js';
import { apiFetch } from '../utils/apiFetch';
import { useParams, useNavigate } from 'react-router-dom';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, PieController, ArcElement);

interface Statistic {
    hour: number;
    total: number;
    accepted: number;
    not_accepted: number;
    invalid_code: number;
    CTSTREET_accepted: number;
    ONLINE_accepted: number;
    WORKER_accepted: number;
    FREE_accepted: number;
    CTSTREET_not_accepted: number;
    ONLINE_not_accepted: number;
    WORKER_not_accepted: number;
    FREE_not_accepted: number;
}

const EstadisticasEvento: React.FC = () => 
{
    const { eventId, clientId } = useParams();
    const navigate = useNavigate();
    const { token } = useContext(AuthContext);
    const eventIdNum = Number(eventId);
    const [statistics, setStatistics] = useState<Statistic[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [title, setTitle] = useState<string>('');

    // Tipos de índice válidos para Statistic
    type AcceptedOrNot = 'accepted' | 'not_accepted';
    type TypeKeys = 'CTSTREET' | 'ONLINE' | 'WORKER' | 'FREE';
    const CTSTREET_color = '#c2c2f0';
    const ONLINE_color = '#ffcccb';
    const WORKER_color = '#f0e2c2';
    const FREE_color = '#a2d9ce';
    const invalid_color = 'red';

    // Mapea los tipos de índice a las claves válidas
    type StatisticKeys = `${TypeKeys}_${AcceptedOrNot}`;

    //---------------------------------------------------------

    const fetchStatistics = useCallback(async () => 
    {
        setLoading(true);
        try 
        {
            const queryParams = new URLSearchParams({ 
                id_event: eventIdNum.toString(),
                id_client: clientId || ''
            });
            
            const response = await apiFetch(`web/event_statistics?${queryParams.toString()}`, {
                method: 'GET'
            });

            if (response.ok) 
            {
                const data = await response.json();
                
                setTitle(data.title);
                
                if (Array.isArray(data.statistics)) 
                {
                    setStatistics(data.statistics);
                } 
                else 
                {
                    throw new Error('Datos inesperados recibidos.');
                }
            } 
            else 
            {
                navigate('/main/not-found');
            }
        } 
        catch (error) 
        {
            const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
            setError(errorMsg);
        } 
        finally 
        {
            setLoading(false);
        }
    }, [eventId, token]);

    //---------------------------------------------------------

    useEffect(() => 
    {
        fetchStatistics();
    }, [fetchStatistics]);

    //---------------------------------------------------------

    const generateChartData = (dataKey: keyof Statistic, color: string) => {
        return {
            labels: statistics.map(stat => `${stat.hour}`),
            datasets: [{
                label: 'Escaneos',
                data: statistics.map(stat => stat[dataKey]),
                backgroundColor: color,
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1,
            }],
        };
    };
    
        
    //---------------------------------------------------------

    const chartOptions = {
        responsive: true, // Habilita el redimensionamiento automático
        maintainAspectRatio: false, // Permite que el gráfico use el tamaño del contenedor        
        plugins: 
        {
            legend: 
            {
                display: false,
            },
            tooltip: 
            {
                callbacks: {
                    title: (tooltipItems: any) => {
                        const hour = tooltipItems[0].label;
                        const nextHour = (parseInt(hour) + 1) % 24;
                        return `${hour}h - ${nextHour}h`;
                    },
                    label: (tooltipItem: any) => {
                        const value = tooltipItem.raw;
                        return value === 1 ? '1 escaneo' : `${value} escaneos`;
                    },
                },
                displayColors: false,
            },
        },
        scales: 
        {
            x: 
            {
                ticks: 
                {
                    callback: function(value: string | number) {
                        return `${value}h`;
                    },
                    padding: 10,
                },
                grid: 
                {
                    display: false, // Oculta las líneas verticales
                },
            },
            y: 
            {
                ticks: 
                {
                    stepSize: 1,
                    beginAtZero: true,
                },
                grid: 
                {
                    display: false, // Oculta las líneas verticales
                },
            },
        },
    };

    //---------------------------------------------------------

    const generate_pie_chart_data_aceptados = (acceptedOrNot: AcceptedOrNot) => 
    {
        const types: { [key in TypeKeys]: string } = {
            'CTSTREET': 'CTSTREET',
            'ONLINE': 'ONLINE',
            'WORKER': 'WORKER',
            'FREE' : 'FREE'
        };
        
        const typeKeys: TypeKeys[] = ['CTSTREET', 'ONLINE', 'WORKER', 'FREE'];
        const totals = typeKeys.map(type => statistics.reduce((sum, stat) => sum + stat[`${type}_${acceptedOrNot}` as StatisticKeys], 0));
        
        return {
            labels: typeKeys.map(type => types[type]),
            datasets: [{
                data: totals,
                backgroundColor: [CTSTREET_color, ONLINE_color, WORKER_color, FREE_color],
            }],
        };
    };

    const generate_pie_chart_data_no_aceptados = (acceptedOrNot: AcceptedOrNot) => 
    {
        const types: { [key in TypeKeys | 'invalid_code']: string } =
        {
            'CTSTREET': 'CTSTREET',
            'ONLINE': 'ONLINE',
            'WORKER': 'WORKER',
            'FREE' : 'FREE',
            'invalid_code': 'Inválidos'
        };

        const typeKeys: (TypeKeys | 'invalid_code')[] = ['CTSTREET', 'ONLINE', 'WORKER', 'FREE', 'invalid_code'];
        const totals = typeKeys.map(type => statistics.reduce((sum, stat) => sum + (type === 'invalid_code' ? stat.invalid_code : stat[`${type}_${acceptedOrNot}` as StatisticKeys]), 0));
        
        return {
            labels: typeKeys.map(type => types[type]),
            datasets: [{
                data: totals,
                backgroundColor: [CTSTREET_color, ONLINE_color, WORKER_color, FREE_color, invalid_color],
            }],
        };
    };

    //---------------------------------------------------------    

    const pieChartOptions: ChartOptions<'pie'> = 
    {
        plugins:
        {
            legend: 
            {
                display: true,
                position: 'bottom',
                labels: 
                {
                    color: '#333', // Color del texto de la leyenda
                    boxWidth: 20, // Ancho del cuadro de color
                },
            },
            tooltip: 
            {
                callbacks: 
                {
                    label: (tooltipItem: any) => 
                    {
                        const value = tooltipItem.raw;
                        return value === 1 ? '1 escaneo' : `${value} escaneos`;
                    },
                },
                displayColors: false,
            },
        },
    };

    //---------------------------------------------------------   

    const calculateTotal = (dataKey: keyof Statistic) => 
    {
        return statistics.reduce((sum, stat) => sum + stat[dataKey], 0);
    };

    //---------------------------------------------------------   

    const acceptedTotals = 
    {
        CTSTREET: calculateTotal('CTSTREET_accepted'),
        ONLINE: calculateTotal('ONLINE_accepted'),
        WORKER: calculateTotal('WORKER_accepted'),
        FREE: calculateTotal('FREE_accepted')
    };

    const notAcceptedTotals = 
    {
        CTSTREET: calculateTotal('CTSTREET_not_accepted'),
        ONLINE: calculateTotal('ONLINE_not_accepted'),
        WORKER: calculateTotal('WORKER_not_accepted'),
        FREE: calculateTotal('FREE_not_accepted'),
        invalidos: calculateTotal('invalid_code')
    };

    const invalidTotals = calculateTotal('invalid_code');

    //---------------------------------------------------------

    return (
        <div className="escaneos-container">
            <div className="miguitas">Estadísticas de {title}</div>
            <div className="content-wrapper">
                 <div className="cod-toolbar">
                    <button
                        className="icon-btn"
                        onClick={() => navigate(`/main/recintos/${clientId}/eventos`)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 19l-7-7 7-7"/></svg>
                        Volver a Eventos
                    </button>
                </div>
                <div className="eventos-container">
                    {error && <p className="error-message">{error}</p>}
                    {loading && !error && <p className="loading-message">Obteniendo estadísticas...</p>}
                    {!loading && !error && statistics.length === 0 && <p>No hay estadísticas para mostrar.</p>}
                    {!loading && !error && statistics.length > 0 && (
                        <>
                            <div className="chart-group">
                                <h3>TOTALES : {(acceptedTotals.CTSTREET + acceptedTotals.ONLINE + acceptedTotals.WORKER + acceptedTotals.FREE + notAcceptedTotals.CTSTREET + notAcceptedTotals.ONLINE + notAcceptedTotals.WORKER + notAcceptedTotals.FREE + notAcceptedTotals.invalidos).toLocaleString('es-ES')} escaneos</h3>
                                <div className="chart-grid">
                                    <div className="chart-wrapper">
                                        <Bar data={generateChartData('total', '#adeece')} options={chartOptions} />
                                    </div>
                                </div>
                            </div>
                            <div className="chart-group">
                                <h3>ACEPTADOS : {(acceptedTotals.CTSTREET + acceptedTotals.ONLINE + acceptedTotals.WORKER + acceptedTotals.FREE).toLocaleString('es-ES')} escaneos</h3>
                                <div className="chart-grid">
                                    <div className="fila">
                                        <div className="pie-wrapper">
                                            <Pie data={generate_pie_chart_data_aceptados('accepted')} options={pieChartOptions} />
                                        </div> 
                                        <div className="chart-wrapper">
                                            <Bar data={generateChartData('accepted', '#adeece')} options={chartOptions} />
                                        </div> 
                                    </div>   
                                    <div className="fila">
                                        <div className="chart-wrapper">
                                            <h4>CTSTREET : {acceptedTotals.CTSTREET.toLocaleString('es-ES')} escaneos</h4>
                                            <Bar data={generateChartData('CTSTREET_accepted', CTSTREET_color)} options={chartOptions} />
                                        </div>
                                        <div className="chart-wrapper">
                                            <h4>ONLINE : {acceptedTotals.ONLINE.toLocaleString('es-ES')} escaneos</h4>
                                            <Bar data={generateChartData('ONLINE_accepted', ONLINE_color)} options={chartOptions} />
                                        </div>
                                        <div className="chart-wrapper">
                                            <h4>WORKER : {acceptedTotals.WORKER.toLocaleString('es-ES')} escaneos</h4>
                                            <Bar data={generateChartData('WORKER_accepted', WORKER_color)} options={chartOptions} />
                                        </div>
                                        <div className="chart-wrapper">
                                            <h4>FREE : {acceptedTotals.FREE.toLocaleString('es-ES')} escaneos</h4>
                                            <Bar data={generateChartData('FREE_accepted', FREE_color)} options={chartOptions} />
                                        </div>
                                    </div>                               
                                </div>
                            </div>
                            <div className="chart-group">
                                <h3>NO ACEPTADOS : {(notAcceptedTotals.CTSTREET + notAcceptedTotals.ONLINE + notAcceptedTotals.WORKER + notAcceptedTotals.FREE + notAcceptedTotals.invalidos).toLocaleString('es-ES')} escaneos</h3>
                                <div className="chart-grid">
                                    <div className="fila">
                                        <div className="pie-wrapper">
                                            <Pie data={generate_pie_chart_data_no_aceptados('not_accepted')} options={pieChartOptions} />
                                        </div>      
                                        <div className="chart-wrapper">
                                            <Bar data={generateChartData('not_accepted', '#adeece')} options={chartOptions} />
                                        </div>
                                    </div>
                                    <div className="fila">                              
                                        <div className="chart-wrapper">
                                            <h4>CTSTREET : {notAcceptedTotals.CTSTREET.toLocaleString('es-ES')} escaneos</h4>
                                            <Bar data={generateChartData('CTSTREET_not_accepted', CTSTREET_color)} options={chartOptions} />
                                        </div>
                                        <div className="chart-wrapper">
                                            <h4>ONLINE : {notAcceptedTotals.ONLINE.toLocaleString('es-ES')} escaneos</h4>
                                            <Bar data={generateChartData('ONLINE_not_accepted', ONLINE_color)} options={chartOptions} />
                                        </div>
                                        <div className="chart-wrapper">
                                            <h4>WORKER : {notAcceptedTotals.WORKER.toLocaleString('es-ES')} escaneos</h4>
                                            <Bar data={generateChartData('WORKER_not_accepted', WORKER_color)} options={chartOptions} />
                                        </div>
                                        <div className="chart-wrapper">
                                            <h4>FREE : {notAcceptedTotals.FREE.toLocaleString('es-ES')} escaneos</h4>
                                            <Bar data={generateChartData('FREE_not_accepted', FREE_color)} options={chartOptions} />
                                        </div>                                        
                                        <div className="chart-wrapper">
                                            <h4>Inválidos : {invalidTotals.toLocaleString('es-ES')} escaneos</h4>
                                            <Bar data={generateChartData('invalid_code', 'red')} options={chartOptions} />
                                        </div>                                        
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EstadisticasEvento;
